import { memo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { IconButton, Menu, Text } from 'react-native-paper';

import { StatusBadge } from '@/components/atoms/badge';
import {
  ActionLinkRow,
  type ActionLinkItem,
} from '@/components/molecules/action-link/ActionLinkRow';
import { formatDate } from '@/formatters/formatDate';
import { formatDateOnly } from '@/formatters/formatDateOnly';
import { useTranslation } from '@/i18n';
import { type UploadStatus } from '@/models/voiceRecording';
import { iconSize, radii, spacing, unisonColors } from '@/theme';

interface VoiceRecordingLocalItemProps {
  readonly recordedAt: string;
  readonly fileSize: string;
  readonly locale: string;
  readonly uploadStatus: UploadStatus;
  readonly deleteLabel: string;
  readonly onDelete: () => void;
  readonly onTurnOnWifi?: () => void;
  readonly onChangeUploadSettings?: () => void;
  readonly onRetry?: () => void;
}

type BadgeVariant = 'error' | 'warning' | 'info';

interface BadgeConfig {
  readonly variant: BadgeVariant;
  readonly label: string;
  readonly loading?: boolean;
}

const getBadge = (
  uploadStatus: UploadStatus,
  t: (key: string, options?: Record<string, unknown>) => string,
): BadgeConfig | null => {
  switch (uploadStatus) {
    case 'pending':
      return { variant: 'error', label: t('meetings.pendingUpload') };
    case 'failed':
      return { variant: 'error', label: t('meetings.uploadFailed') };
    case 'uploading':
      return { variant: 'info', label: t('drawer.status.uploading'), loading: true };
    case 'pending_contact':
      return { variant: 'warning', label: t('drawer.status.pendingContact') };
    default:
      return null;
  }
};

const getActions = (
  uploadStatus: UploadStatus,
  t: (key: string, options?: Record<string, unknown>) => string,
  onTurnOnWifi?: () => void,
  onChangeUploadSettings?: () => void,
  onRetry?: () => void,
): ActionLinkItem[] => {
  switch (uploadStatus) {
    case 'pending':
      return [
        { label: t('meetings.turnOnWifi'), onPress: onTurnOnWifi ?? (() => {}) },
        {
          label: t('meetings.changeUploadSettings'),
          onPress: onChangeUploadSettings ?? (() => {}),
        },
      ];
    case 'failed':
    case 'uploading':
      return [{ label: t('recording.actions.retry'), onPress: onRetry ?? (() => {}) }];
    default:
      return [];
  }
};

const VoiceRecordingLocalItemBase = ({
  recordedAt,
  fileSize,
  locale,
  uploadStatus,
  deleteLabel,
  onDelete,
  onTurnOnWifi,
  onChangeUploadSettings,
  onRetry,
}: VoiceRecordingLocalItemProps) => {
  const { t } = useTranslation();
  const [menuVisible, setMenuVisible] = useState(false);

  const handleMenuOpen = () => setMenuVisible(true);
  const handleMenuClose = () => setMenuVisible(false);

  const handleDelete = () => {
    setMenuVisible(false);
    onDelete();
  };

  const badge = getBadge(uploadStatus, t);
  const actions = getActions(uploadStatus, t, onTurnOnWifi, onChangeUploadSettings, onRetry);
  const title = formatDate(recordedAt, locale);
  const dateOnly = formatDateOnly(recordedAt);
  const dateTimeText = t('settings.storage.dateAndTime', { date: dateOnly });
  const sizeText = t('settings.storage.sizeAndRecorded', { size: fileSize, date: dateOnly });

  return (
    <View
      style={[styles.card, actions.length === 0 && styles.cardNoActions]}
      testID="recording-card">
      <View style={styles.contentRow}>
        <View style={styles.textContainer}>
          {badge ? (
            <StatusBadge
              label={badge.label}
              variant={badge.variant}
              loading={badge.loading}
              testID="recording-status-badge"
            />
          ) : null}
          <View style={styles.textContent}>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.detail}>{dateTimeText}</Text>
            <Text style={styles.detail}>{sizeText}</Text>
          </View>
        </View>
        <Menu
          visible={menuVisible}
          onDismiss={handleMenuClose}
          contentStyle={styles.menuContent}
          anchor={
            <IconButton
              icon="dots-vertical"
              onPress={handleMenuOpen}
              size={iconSize.md}
              style={styles.menuButton}
            />
          }>
          <Menu.Item
            onPress={handleDelete}
            title={deleteLabel}
            leadingIcon="delete"
            style={styles.menuItem}
          />
        </Menu>
      </View>
      {actions.length > 0 ? <ActionLinkRow actions={actions} testID="recording-actions" /> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: unisonColors.white,
    borderRadius: radii.md,
    paddingTop: spacing[4],
    paddingHorizontal: spacing[4],
    rowGap: spacing[1],
  },
  cardNoActions: {
    paddingBottom: spacing[4],
  },
  contentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  textContainer: {
    flex: 1,
    gap: spacing[2],
  },
  textContent: {
    gap: 3,
  },
  title: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    letterSpacing: 0.5,
    color: unisonColors.textNdPrimary,
  },
  detail: {
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0.25,
    color: unisonColors.textNdSecondary,
  },
  menuButton: {
    alignSelf: 'flex-start',
    margin: 0,
    marginTop: -8,
    marginRight: -8,
  },
  menuContent: {
    minWidth: 0,
  },
  menuItem: {
    paddingRight: 4,
  },
});

export const VoiceRecordingLocalItem = memo(VoiceRecordingLocalItemBase);
